export type ExternalProvider =
  | "None"
  | "Google"
  | "Microsoft"
  | "MicrosoftExchange"
  | "Zoom"
  | "Webex"
  | "Mago"
  | "OpenVisio"
  | "LiveCollaboration";

export type FeatureType =
  | "Default"
  | "JoinMeeting"
  | "Calendar"
  | "AuthCall"
  | "HostMeeting"
  | "Chat";

export type FeatureConfig = {
  calendar: {
    calendarId?: string;
  };
};

export type ExchangeConfigurationForm = {
  exchangeServerUrl: string;
  accountName: string;
  accountPassword: string;
  accountRoomName: string;
  accountDomainName: string;
  exchangeVersion: string;
};

export const createExchangeConfigurationForm =
  (): ExchangeConfigurationForm => ({
    exchangeServerUrl: "",
    accountName: "",
    accountPassword: "",
    accountRoomName: "",
    accountDomainName: "",
    exchangeVersion: "Exchange2016",
  });

/**
 * The Live Collaboration account typed in the Connect dialog. It is sent once with the
 * provider's Default feature, together with the server address, which is ours (see
 * LIVE_COLLABORATION_CALENDAR_URL); the backend has the room check it, keeps it encrypted, and
 * the room reads the account's BlueMind calendar over CalDAV with it.
 */
export type CalDavConfigurationForm = {
  username: string;
  password: string;
};

export const createCalDavConfigurationForm = (): CalDavConfigurationForm => ({
  username: "",
  password: "",
});

export type FeaturePayload = {
  provider: ExternalProvider;
  feature: FeatureType;
  enabled: boolean;
  config?: FeatureConfig | null;
};

export type ProviderFeature = {
  featureType: FeatureType;
};

export type ProviderAccount = {
  podExternalAccountId: string;
  email?: string;
  provider: ExternalProvider;
  createDate: number;
  features: ProviderFeature[];
  configuration: FeatureConfig;
};

export type PodExternalAccountConfiguration = {
  podExternalAccountConfigurationId: string;
  requireSync: boolean;
  accounts: ProviderAccount[];
};

export type ProviderAccountItem = {
  provider: ExternalProvider;
  slug: string;
  name: string;
  logo: string;
  calendarlogo: string;
  videoconferencelogo: string;
  description: string;
  features: FeatureType[];
  /**
   * When true the provider is only offered to devices that explicitly declare it in
   * their supported capabilities / version-1 wizardSetting; it is excluded from every
   * legacy fallback (devices that never declared capabilities). Old builds in the field
   * predate the capabilities handshake, so a provider without this flag is assumed
   * supported by them — set it on every provider added after the handshake shipped.
   */
  requiresCapability?: boolean;
};

export type SupportedCapabilitiesExternalAccount = {
  [slug: string]: {
    calendar?: Record<string, unknown>;
    meeting?: {
      host?: Record<string, unknown>;
      join?: Record<string, unknown>;
      authCall?: Record<string, unknown>;
    };
  };
};

export type SupportedCapabilities = {
  version: number;
  externalAccount?: SupportedCapabilitiesExternalAccount;
  policy?: Record<string, unknown>;
  collaboration?: Record<string, unknown>;
  calendar?: Record<string, unknown>;
  videoCall?: Record<string, unknown>;
};

export type ProviderAccountFeature = {
  provider?: ExternalProvider;
  name: string;
  logo: string;
  description: string;
  feature: FeatureType;
  account?: ProviderAccount;
  enabled: boolean;
};

export type GoogleCalendarItem = {
  id: string;
  summary: string;
  primary?: boolean;
};

export function ExternalAccountsDeserializer(
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types */
  input: any
): PodExternalAccountConfiguration {
  return {
    podExternalAccountConfigurationId: input.podExternalAccountConfigurationId,
    requireSync: input.requireSync,
    accounts: input.accounts.map(ProviderAccountDeserializer),
  };
}

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types */
function ProviderAccountDeserializer(input: any): ProviderAccount {
  const configuration =
    input.configuration && Object.keys(input.configuration).length > 0
      ? input.configuration
      : undefined;
  return {
    podExternalAccountId: input.podExternalAccountId,
    email: input.email,
    provider: input.provider,
    createDate: input.createDate,
    features: input.features,
    configuration: configuration,
  };
}

// {
//         "podExternalAccountConfigurationId": "32162af9-02ab-4115-bdf5-b3da59e04fc2",
//         "requireSync": true,
//         "accounts": [
//             {
//                 "podExternalAccountId": "69e9b09f-626f-4227-807a-da8eef841937",
//                 "email": "jancarlos.morillo@remago.com",
//                 "provider": 1,
//                 "createDate": 1766486201499,
//                 "features": []
//             }
//         ]
//     },

// {
//   "podExternalAccountConfigurationId": "7f3a9c2e-4c3d-4a58-9b1f-6e9c5d7b1a21",
//   "requireSync": true,
//   "accounts": [
//     {
//       "podExternalAccountId": "a1b2c3d4-1111-4f88-9a9a-1234567890ab",
//       "email": "user@gmail.com",
//       "provider": "Google",
//       "createDate": 1765801331,
//       "features": [
//         {
//           "featureType": "Calendar"
//         },
//         {
//           "featureType": "InstantMeeting"
//         }
//       ]
//     },
//     {
//       "podExternalAccountId": "b2c3d4e5-2222-4a77-8b8b-abcdefabcdef",
//       "email": "user@outlook.com",
//       "provider": "Microsoft",
//       "createDate": 1765891127,
//       "features": [
//         {
//           "featureType": "InstantMeeting"
//         },
//         {
//           "featureType": "Calendar"
//         }
//       ]
//     }
//   ]
// }
